import { System } from '../System';
import { Entity } from '../Entity';
import { TransformComponent } from '../components/TransformComponent';
import { HealthComponent } from '../components/HealthComponent';
import { SpriteComponent } from '../components/SpriteComponent';

/**
 * HealthBarSystem - Renders health bars above entities
 * Shows health bar when entity is damaged or in combat
 */
export class HealthBarSystem extends System {
  public readonly requiredComponents = ['transform', 'health', 'sprite'];

  /** Map of entity ID to health bar graphics */
  private healthBars: Map<string, Phaser.GameObjects.Graphics> = new Map();

  /** Health bar dimensions */
  private barWidth = 40;
  private barHeight = 6;
  private barOffsetY = -10; // Offset above sprite

  /** Reference to tilemap layer for coordinate conversion */
  private groundLayer: Phaser.Tilemaps.TilemapLayer | null = null;

  /** Tile dimensions for manual calculation fallback */
  private tileWidth = 64;
  private tileHeight = 32;

  /** Map offset for manual calculation */
  private mapOffsetX = 0;
  private mapOffsetY = 0;

  public init(): void {
    const camera = this.world.getScene().cameras.main;
    this.mapOffsetX = camera.width / 2;
    this.mapOffsetY = 200;
  }

  /**
   * Set tilemap layer for coordinate conversion
   */
  public setTilemapLayer(layer: Phaser.Tilemaps.TilemapLayer | null): void {
    this.groundLayer = layer;
  }

  /**
   * Set map offset for manual coordinate conversion
   */
  public setMapOffset(x: number, y: number): void {
    this.mapOffsetX = x;
    this.mapOffsetY = y;
  }

  public update(entities: Entity[], _delta: number): void {
    for (const entity of entities) {
      this.updateHealthBar(entity);
    }
  }

  /**
   * Convert world coordinates to screen position
   */
  private worldToScreenPos(worldX: number, worldY: number): { x: number; y: number } {
    if (this.groundLayer) {
      const pos = this.groundLayer.tileToWorldXY(worldX, worldY);
      if (pos) {
        return {
          x: pos.x + this.tileWidth / 2,
          y: pos.y + this.tileHeight / 2,
        };
      }
    }

    // Fallback to manual isometric calculation
    const screenX = (worldX - worldY) * (this.tileWidth / 2);
    const screenY = (worldX + worldY) * (this.tileHeight / 2);
    return {
      x: screenX + this.mapOffsetX,
      y: screenY + this.mapOffsetY,
    };
  }

  /**
   * Update health bar for an entity
   */
  private updateHealthBar(entity: Entity): void {
    const transform = entity.getComponent<TransformComponent>('transform');
    const health = entity.getComponent<HealthComponent>('health');
    const sprite = entity.getComponent<SpriteComponent>('sprite');

    if (!transform || !health || !sprite) return;

    // Determine if health bar should be visible
    const shouldShow = this.shouldShowHealthBar(entity, health);

    let bar = this.healthBars.get(entity.id);

    if (!shouldShow) {
      // Hide and remove health bar if it exists
      if (bar) {
        bar.destroy();
        this.healthBars.delete(entity.id);
      }
      return;
    }

    // Create health bar if it doesn't exist
    if (!bar) {
      bar = this.createHealthBar(entity);
      this.healthBars.set(entity.id, bar);
    }

    // Update position
    const screenPos = this.worldToScreenPos(transform.x, transform.y);
    bar.setPosition(
      screenPos.x - this.barWidth / 2,
      screenPos.y + this.barOffsetY - this.barHeight
    );

    // Update health bar fill
    this.drawHealthBar(bar, health, entity);

    // Set depth above sprite
    bar.setDepth((sprite.depth || 0) + 1);
  }

  /**
   * Determine if health bar should be shown
   */
  private shouldShowHealthBar(entity: Entity, health: HealthComponent): boolean {
    // Don't show if dead or fainted
    if (!health.isAlive || health.isFainted) return false;

    // Always show for enemies
    if (entity.hasTag('enemy')) return true;

    // Always show for party member beasties
    if (entity.hasTag('party_member') && entity.hasTag('beastie')) return true;

    // Show if damaged
    if (health.healthPercent < 1) return true;

    // Show if in combat (has recent damage)
    if (health.lastDamageTaken > 0) return true;

    return false;
  }

  /**
   * Create a new health bar graphics object
   */
  private createHealthBar(entity: Entity): Phaser.GameObjects.Graphics {
    const scene = this.world.getScene();
    const bar = scene.add.graphics();

    // Store entity reference for color determination
    (bar as unknown as { entityId: string }).entityId = entity.id;

    return bar;
  }

  /**
   * Draw the health bar
   */
  private drawHealthBar(
    bar: Phaser.GameObjects.Graphics,
    health: HealthComponent,
    entity: Entity
  ): void {
    bar.clear();

    // Background (dark)
    bar.fillStyle(0x000000, 0.7);
    bar.fillRect(0, 0, this.barWidth, this.barHeight);

    // Border
    bar.lineStyle(1, 0x333333, 1);
    bar.strokeRect(0, 0, this.barWidth, this.barHeight);

    // Health fill - red for enemies, green for allies
    const fillWidth = Math.max(0, (this.barWidth - 2) * health.healthPercent);
    const isAlly = entity.hasTag('party_member') || !entity.hasTag('enemy');
    const fillColor = isAlly ? 0x44ff44 : 0xff4444;

    bar.fillStyle(fillColor, 1);
    bar.fillRect(1, 1, fillWidth, this.barHeight - 2);
  }

  public onEntityRemoved(entity: Entity): void {
    const bar = this.healthBars.get(entity.id);
    if (bar) {
      bar.destroy();
      this.healthBars.delete(entity.id);
    }
  }

  public destroy(): void {
    // Clean up all health bars
    for (const bar of this.healthBars.values()) {
      bar.destroy();
    }
    this.healthBars.clear();
  }
}
